import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MapPin, ChevronDown, Globe, X, DollarSign, Check } from 'lucide-react';
import { getTranslation } from '../utils/translations';
import { COUNTRY_DEFAULT_MAPPINGS, isSupportedLanguage, getBestSupportedLanguageForCountry } from '../utils/countryLanguageEngine';
import { useMarket, parseLanguagesJson } from '../context/MarketContext';
import { SUPPORTED_LANGUAGES } from '../utils/countryLanguageEngine';

const RegionSelector = () => {
    const navigate = useNavigate();
    const { 
        country: currentCountryCode, 
        language: currentLanguageCode, 
        currency: currentCurrencyCode, 
        activeCountries, 
        switchMarket 
    } = useMarket();

    const [isOpen, setIsOpen] = useState(false);
    const [selectedCountryCode, setSelectedCountryCode] = useState(() => (currentCountryCode || 'sa').toUpperCase());
    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        const lang = currentLanguageCode || 'ar';
        return isSupportedLanguage(lang) ? lang : 'ar';
    });
    const [selectedCurrency, setSelectedCurrency] = useState(() => currentCurrencyCode || 'SAR');
    const wrapperRef = useRef(null);
    const modalRef = useRef(null);

    // Keep state in sync with market changes
    useEffect(() => {
        setSelectedCountryCode((currentCountryCode || 'sa').toUpperCase());
        const lang = currentLanguageCode || 'ar';
        setSelectedLanguage(isSupportedLanguage(lang) ? lang : 'ar');
        setSelectedCurrency(currentCurrencyCode || 'SAR');
    }, [currentCountryCode, currentLanguageCode, currentCurrencyCode]);

    const availableCurrencies = useMemo(() => [
        'SAR', 'AED', 'KWD', 'QAR', 'BHD', 'OMR', 'USD', 'EUR', 'GBP', 'CAD', 'CHF', 'EGP', 'MAD', 'DZD', 'TND', 'LYD', 'IQD', 'JOD', 'TRY', 'CNY', 'JPY', 'KRW', 'RUB', 'AUD', 'MYR', 'SGD', 'HKD', 'NGN', 'ZAR', 'INR', 'PKR', 'BDT', 'THB', 'IDR', 'PHP', 'VND', 'BRL', 'MXN', 'ARS'
    ], []);

    const selectedCountry = useMemo(() => {
        const found = activeCountries.find(c => c.countryCode?.toUpperCase() === selectedCountryCode.toUpperCase());
        return found || activeCountries[0];
    }, [activeCountries, selectedCountryCode]);

    // Dynamically resolve supported languages for the chosen country
    const countryLanguages = useMemo(() => {
        if (!selectedCountry) return SUPPORTED_LANGUAGES.slice(0, 2);
        const codes = parseLanguagesJson(selectedCountry.supportedLanguages);
        const validCodes = codes.filter(c => isSupportedLanguage(c));
        if (validCodes.length > 0) {
            return SUPPORTED_LANGUAGES.filter(l => validCodes.includes(l.code));
        }
        let defLang = selectedCountry.defaultLanguage;
        if (!isSupportedLanguage(defLang)) {
            defLang = getBestSupportedLanguageForCountry(selectedCountry.countryCode);
        }
        return SUPPORTED_LANGUAGES.filter(l => l.code === defLang || l.code === 'en' || l.code === 'ar');
    }, [selectedCountry]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                if (modalRef.current && modalRef.current.contains(event.target)) {
                    return;
                }
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleCountryChange = (code) => {
        setSelectedCountryCode(code);
        const found = activeCountries.find(c => c.countryCode?.toUpperCase() === code?.toUpperCase());
        const mapping = COUNTRY_DEFAULT_MAPPINGS[code?.toUpperCase()];
        let newLang = found?.defaultLanguage || mapping?.lang || getBestSupportedLanguageForCountry(code);
        if (!isSupportedLanguage(newLang)) {
            newLang = getBestSupportedLanguageForCountry(code);
        }
        const newCurr = found?.defaultCurrency || mapping?.curr || 'USD';

        setSelectedLanguage(newLang);
        setSelectedCurrency(newCurr);
    };

    const handleSave = () => {
        if (!selectedCountry) return;
        let effectiveLang = selectedLanguage;
        if (!isSupportedLanguage(effectiveLang)) {
            effectiveLang = getBestSupportedLanguageForCountry(selectedCountry.countryCode);
        }
        setIsOpen(false);
        switchMarket(selectedCountry.countryCode, effectiveLang, selectedCurrency, navigate);
    };

    if (!selectedCountry) return null;

    return (
        <div ref={wrapperRef} className="region-selector" style={{ position: 'relative', display: 'flex', alignItems: 'center', zIndex: 50 }}>
            <style>{`
                button.lux-region-btn {
                    display: flex !important;
                    align-items: center !important;
                    gap: 8px !important;
                    padding: 6px 14px !important;
                    border-radius: 9999px !important;
                    background: rgba(255, 255, 255, 0.65) !important;
                    border: 1px solid rgba(197, 168, 128, 0.25) !important;
                    cursor: pointer !important;
                    transition: all 0.3s ease !important;
                    outline: none !important;
                    color: #1d1d1f !important;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
                    line-height: normal !important;
                    white-space: nowrap !important;
                }
                body.theme-dark button.lux-region-btn {
                    background: rgba(18, 24, 38, 0.75) !important;
                    border-color: rgba(197, 168, 128, 0.25) !important;
                    color: #f5f5f7 !important;
                }
                button.lux-region-btn:hover {
                    border-color: rgba(197, 168, 128, 0.6) !important;
                    background: rgba(255, 255, 255, 0.95) !important;
                    transform: translateY(-0.5px);
                }
                body.theme-dark button.lux-region-btn:hover {
                    background: rgba(28, 36, 56, 0.9) !important;
                    border-color: rgba(197, 168, 128, 0.5) !important;
                }
                .lux-region-btn-text {
                    font-size: 12.5px !important;
                    font-weight: 600 !important;
                }
                .lux-region-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.65);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    z-index: 999999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 16px;
                }
                .lux-region-modal {
                    background: #ffffff;
                    border: 1px solid rgba(197, 168, 128, 0.3);
                    border-radius: 20px;
                    width: 100%;
                    max-width: 440px;
                    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.3);
                    overflow: hidden;
                    animation: luxModalScale 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }
                body.theme-dark .lux-region-modal {
                    background: #0d121f;
                    border-color: rgba(197, 168, 128, 0.25);
                    color: #ffffff;
                }
                @keyframes luxModalScale {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .lux-region-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 20px;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
                }
                body.theme-dark .lux-region-header {
                    border-bottom-color: rgba(255, 255, 255, 0.08);
                }
                .lux-region-body {
                    padding: 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }
                .lux-region-field {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }
                .lux-region-field label {
                    font-size: 12px;
                    font-weight: 700;
                    color: #86868b;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .lux-region-select {
                    width: 100%;
                    padding: 10px 14px;
                    border-radius: 12px;
                    border: 1px solid rgba(197, 168, 128, 0.3);
                    background: rgba(0, 0, 0, 0.02);
                    font-size: 13.5px;
                    font-weight: 600;
                    color: inherit;
                    outline: none;
                    cursor: pointer;
                }
                body.theme-dark .lux-region-select {
                    background: rgba(255, 255, 255, 0.05);
                    border-color: rgba(197, 168, 128, 0.2);
                }
                .lux-region-footer {
                    padding: 16px 20px;
                    border-top: 1px solid rgba(0, 0, 0, 0.08);
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                }
                body.theme-dark .lux-region-footer {
                    border-top-color: rgba(255, 255, 255, 0.08);
                }
                .lux-region-save-btn {
                    background: #000000;
                    color: #ffffff;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    padding: 9px 22px;
                    border-radius: 12px;
                    font-size: 13.5px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: transform 0.2s ease;
                }
                .lux-region-save-btn:hover {
                    transform: scale(1.03);
                }
            `}</style>

            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className="lux-region-btn"
                aria-label="Regional Settings"
            >
                <span>{selectedCountry.flagEmoji || '🌍'}</span>
                <span className="lux-region-btn-text">
                    {selectedCountry.countryCode} / {selectedLanguage.toUpperCase()} / {selectedCurrency}
                </span>
                <ChevronDown size={11} color="#C5A880" />
            </button>

            {isOpen && createPortal(
                <div className="lux-region-overlay" onClick={() => setIsOpen(false)}>
                    <div ref={modalRef} className="lux-region-modal" onClick={e => e.stopPropagation()}>
                        <div className="lux-region-header">
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{getTranslation('REGIONAL_SETTINGS')}</h3>
                            <button type="button" onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="lux-region-body">
                            {/* Destination Country */}
                            <div className="lux-region-field">
                                <label><MapPin size={13} /> {getTranslation('SHIP_TO')}</label>
                                <select 
                                    className="lux-region-select"
                                    value={selectedCountry?.countryCode || selectedCountryCode}
                                    onChange={(e) => handleCountryChange(e.target.value)}
                                >
                                    {(activeCountries || []).map(c => (
                                        <option key={c.countryCode} value={c.countryCode}>
                                            {c.flagEmoji || '🌍'} {c.nativeName ? `${c.nativeName} (${c.countryName})` : c.countryName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Language Selection */}
                            <div className="lux-region-field">
                                <label><Globe size={13} /> {getTranslation('LANGUAGE')}</label>
                                <select
                                    className="lux-region-select"
                                    value={selectedLanguage}
                                    onChange={(e) => setSelectedLanguage(e.target.value)}
                                >
                                    {SUPPORTED_LANGUAGES.map(l => (
                                        <option key={l.code} value={l.code}>
                                            {l.flag} {l.nativeName} ({l.name})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Currency Selection */}
                            <div className="lux-region-field">
                                <label><DollarSign size={13} /> {getTranslation('CURRENCY')}</label>
                                <select
                                    className="lux-region-select"
                                    value={selectedCurrency}
                                    onChange={(e) => setSelectedCurrency(e.target.value)}
                                >
                                    {availableCurrencies.map(curr => (
                                        <option key={curr} value={curr}>{curr}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="lux-region-footer">
                            <button type="button" className="lux-region-save-btn" onClick={handleSave}>
                                {getTranslation('SAVE')}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default RegionSelector;
